import { useState, useEffect, useContext } from "react"
import { bool, number, shape, array, func } from "prop-types"
import { ReservationContext } from "source/registrations/reservations/ReservationContext"
import { TextInput } from "@planningcenter/doxy-web"
import { Select } from "source/shared/components"
import { Checkbox } from "source/registrations/CheckableInput"
import Field from "./Field"
import { formatEmergencyContact } from "./emergencyContact"

const newEmergencyContact = { id: "new", name: "", phoneNumber: "" }

const EmergencyContactField = ({
  attendee,
  onChange,
  reservation,
  isRequired,
  isOptional,
}) => {
  const { emergencyContacts, setEmergencyContacts } =
    useContext(ReservationContext)

  const bestAvailableEmergencyContact = () => {
    const attendeeEmergencyContacts = emergencyContacts.filter(
      (contact) => contact.name !== attendee.person?.name,
    )

    const responsiblePersonEmergencyContact = attendeeEmergencyContacts.find(
      (contact) => contact.name === reservation.responsiblePerson.name,
    )

    return (
      responsiblePersonEmergencyContact ||
      attendeeEmergencyContacts[0] ||
      newEmergencyContact
    )
  }

  const [error, setError] = useState(null)
  const [name, setName] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [useSameEmergencyContact, setUseSameEmergencyContact] = useState(false)
  const [emergencyContact, setEmergencyContact] = useState(
    isRequired ? bestAvailableEmergencyContact() : null,
  )

  useEffect(() => {
    if (attendee.emergencyContact) {
      setEmergencyContact(attendee.emergencyContact)
    }
  }, [attendee.emergencyContact])

  useEffect(() => {
    const attendeeEmergencyContact =
      attendee.emergencyContact || emergencyContact

    if (!attendeeEmergencyContact || attendeeEmergencyContact?.id === "new")
      return

    const updatedEmergencyContact =
      emergencyContacts.find(
        (ec) =>
          ec.id === attendeeEmergencyContact.id ||
          ec.name === attendeeEmergencyContact.name,
      ) || bestAvailableEmergencyContact()

    if (updatedEmergencyContact) {
      setEmergencyContact(updatedEmergencyContact)
    }
  }, [emergencyContacts])

  const onEmergencyContactChange = ({ target }) => {
    const emergencyContact =
      target.value === "new"
        ? newEmergencyContact
        : emergencyContacts.find((ec) => ec.id === target.value)

    onChange("emergencyContact", {
      ...emergencyContact,
      isValid: false,
    })
  }

  const handleSave = (onEditingClose) => {
    let updatedEmergencyContacts = []
    const ecName = emergencyContact?.name || name
    const ecPhoneNumber = emergencyContact?.phoneNumber || phoneNumber

    if (!ecName || !ecPhoneNumber)
      return setError("Name and phone number are required")

    const attendeeEmergencyContact = formatEmergencyContact(
      ecName,
      ecPhoneNumber,
    )

    if (emergencyContact.id === "new") {
      updatedEmergencyContacts = [
        ...emergencyContacts,
        attendeeEmergencyContact,
      ]
    } else {
      updatedEmergencyContacts = emergencyContacts.map((ec) =>
        ec.id === emergencyContact?.id
          ? { ...ec, ...attendeeEmergencyContact }
          : ec,
      )
    }

    onChange(
      "emergencyContact",
      {
        ...attendeeEmergencyContact,
        isValid: true,
      },
      useSameEmergencyContact,
    )

    setEmergencyContacts(updatedEmergencyContacts)
    setName("")
    setPhoneNumber("")
    setUseSameEmergencyContact(false)
    setError(null)
    onEditingClose()
  }

  const handleEdit = () => {
    const attendeeEmergencyContactId =
      attendee.emergencyContact?.id || emergencyContact?.id

    const attendeeEmergencyContact =
      emergencyContacts.find((x) => x.id === attendeeEmergencyContactId) ||
      bestAvailableEmergencyContact()

    onChange("emergencyContact", {
      ...attendeeEmergencyContact,
      isValid: false,
    })
  }

  const handleCancel = () => {
    onChange("emergencyContact", null)
    setEmergencyContact(null)
  }

  const persistedEmergencyContact = attendee?.emergencyContact?.isValid
    ? attendee.emergencyContact
    : null

  return (
    <Field
      label="Emergency Contact"
      value={emergencyContact?.name || persistedEmergencyContact?.name}
      persistedValue={persistedEmergencyContact?.name || ""}
      onEdit={handleEdit}
      onSave={handleSave}
      onCancel={handleCancel}
      error={error}
      isRequired={isRequired}
      isOptional={isOptional}
      isEditable
    >
      <div className="d-f fd-c g-1">
        <Select
          name={`emergencyContact-${attendee.id}`}
          value={emergencyContact?.id || ""}
          options={[
            ...emergencyContacts.map((ec) => ({
              label: ec.name,
              value: ec.id,
            })),
            { label: "Someone else", value: "new" },
          ]}
          onChange={onEmergencyContactChange}
          style={{ height: "32px" }}
        />

        {!emergencyContact?.name && (
          <TextInput
            placeholder="Name"
            size="sm"
            onChange={({ target: { value } }) => setName(value)}
          />
        )}

        {!emergencyContact?.phoneNumber && (
          <TextInput
            placeholder="Phone Number"
            size="sm"
            onChange={({ target: { value } }) => setPhoneNumber(value)}
          />
        )}

        {reservation.attendees.length > 1 && (
          <Checkbox
            label="Use this contact for all attendees"
            id={`sameContact_${attendee.id}`}
            name={`sameContact_${attendee.id}`}
            onChange={({ target }) =>
              setUseSameEmergencyContact(target.checked)
            }
          />
        )}
      </div>
    </Field>
  )
}

EmergencyContactField.propTypes = {
  attendee: shape({}),
  reservation: shape({
    responsiblePerson: shape({
      id: number,
    }),
    attendees: array,
  }),
  onChange: func,
  totalAttendees: number,
  isRequired: bool,
  isOptional: bool,
}

export default EmergencyContactField
